<!-- SocialListsBox.vue -->
<template>
  <div v-if="visible" class="overlay" @click.self="close">
    <div class="social-lists-box">
      <div class="box-header">
        <h1 class="box-title">People</h1>
        <button class="close-btn" @click="close" aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6 6 18M6 6l12 12" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="box-body">
        <SocialSection
          title="Friends"
          :items="friends.items"
          :loading="friends.loading"
          :hasMore="friends.hasMore"
          @load-more="loadMore('friends')"
          @toggle-follow="onToggleFollow"
          @showProfile="(usr)=>profile.show(usr)"
        />
        <SocialSection
          title="Following"
          :items="following.items"
          :loading="following.loading"
          :hasMore="following.hasMore"
          @load-more="loadMore('following')"
          @toggle-follow="onToggleFollow"
          @showProfile="(usr)=>profile.show(usr)"
        />
        <SocialSection
          title="Followers"
          :items="followers.items"
          :loading="followers.loading"
          :hasMore="followers.hasMore"
          @load-more="loadMore('followers')"
          @toggle-follow="onToggleFollow"
          @showProfile="(usr)=>profile.show(usr)"
        />
      </div>
    </div>
    <Profile :visible="false" ref="profile"/>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { reactive, ref, inject } from 'vue';
import SocialSection from './SocialSection.vue';
import Profile from "@/views/profile/Profile.vue"

const axios = inject('axios')
const visible = ref(false)
const profile = ref(null)

const PAGE_LIMIT = 10;

function makeSectionState() {
  return { items: [], page: 0, loading: false, hasMore: true, loaded: false };
}

const friends = reactive(makeSectionState());
const following = reactive(makeSectionState());
const followers = reactive(makeSectionState());

const sectionMap = { friends, following, followers };
const typeMap = {
  friends: 'friendList',
  following: 'followingList',
  followers: 'followerList',
};

async function fetchPage(kind) {
  const state = sectionMap[kind];
  if (state.loading || !state.hasMore) return;

  state.loading = true;
  try {
    const { data } = await axios.post('friendship.php', {
      type: typeMap[kind],
      page: state.page,
      limit: PAGE_LIMIT,
    });

    state.items.push(...data);
    state.page += 1;
    if (data.length < PAGE_LIMIT) state.hasMore = false;
  } catch (e) {
    console.error(`Failed to load ${kind}`, e);
  } finally {
    state.loading = false;
  }
}

function loadMore(kind) {
  fetchPage(kind);
}

async function onToggleFollow({ uid, isFollowing }) {
  try {
    await axios.post('friendship.php', {
      type: isFollowing ? 'unfollow' : 'follow',
      following: uid
    });

    for (const state of Object.values(sectionMap)) {
      const item = state.items.find((i) => i.uid === uid);
      if (item) item.isFollowing = !isFollowing;
    }
  } catch (e) {
    console.error('Follow toggle failed', e);
  }
}

function close() {
  if(profile.value.Display ) {
    profile.value.hide()
    return false
  }
  visible.value = false
}
function show () {
  visible.value = true
  loadAllIfNeeded()
}
function loadAllIfNeeded() {
  for (const kind of Object.keys(sectionMap)) {
    if (!sectionMap[kind].loaded) {
      sectionMap[kind].loaded = true;
      fetchPage(kind);
    }
  }
}

defineExpose({show, close, visible})
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.social-lists-box {
  background: #0b0f1f;
  border-radius: 16px;
  width: min(480px, 92vw);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #1e2740;
}

.box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #1e2740;
  flex-shrink: 0;
}

.box-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.close-btn:hover {
  background: #1c2440;
  color: #ffffff;
}

.box-body {
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>